/**
 * My Commissions — self-service dashboard for recruiters and salespeople.
 *
 * Same page for both jobs. Role only changes the heading and the sales
 * transition note. Date filter applies to GP, hours, trend, and contractors.
 */

import { api, type GoalPortalData, type MyCommissionData } from './api';
import { UnifiedTimeFilter } from './components/UnifiedTimeFilter';
import { useTimeFilter } from './context/TimeFilterContext';
import { KpiCard, computeTrend } from './components/KpiCard';
import { Card, CardHeader, Cell, Row, Spinner, Table } from './components/Ui';
import { hours, money, moneyCompact, rate, shortDate, toPlotValue } from './format';
import { useAsyncData } from './hooks/useAsyncData';
import { GoalProgress } from './reports/GoalProgress';
import { GoalWeekTable } from './reports/GoalWeekTable';
import { Shell, type ShellProps } from './Shell';

function FillBar({
  pct,
  left,
  right,
  tone,
}: {
  pct: number;
  left: string;
  right: string;
  tone: 'profit' | 'caution';
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="w-full">
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
        <div
          className={`h-full rounded-full ${tone === 'profit' ? 'bg-profit' : 'bg-caution'}`}
          style={{ width: `${clamped.toFixed(1)}%` }}
        />
      </div>
      <div className="mt-0.5 flex justify-between text-[9px] text-ink-faint">
        <span>{left}</span>
        <span>{right}</span>
      </div>
    </div>
  );
}

function perHour(gp: string, hrs: string): number {
  const h = toPlotValue(hrs);
  if (h === 0) return 0;
  return toPlotValue(gp) / h;
}

export function MyCommissions(props: ShellProps) {
  const tf = useTimeFilter();

  const { data, error, loading } = useAsyncData(
    () => api.myCommission(tf.toDateRange()),
    [tf.selection],
    'Could not load commission data.',
  );
  const { data: goals } = useAsyncData<GoalPortalData>(
    () => api.goalPortal({ subject: 'recruiter' }),
    [],
    'Could not load personal goals.',
  );

  const heading = data && !('error' in data) ? data.recruiter : null;

  return (
    <Shell {...props}>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">{heading?.name ?? 'My Commissions'}</h1>
          <p className="mt-1 text-sm text-ink-faint capitalize">
            {heading !== null ? `${heading.role} · My Commissions` : 'My Commissions'}
          </p>
        </div>
        <UnifiedTimeFilter />
      </div>

      {error && (
        <Card>
          <div className="px-4 py-8 text-center">
            {error.includes('not_linked') || error.includes('not linked') ? (
              <>
                <p className="mb-2 text-lg font-semibold text-ink">Account Setup Needed</p>
                <p className="text-sm text-ink-muted">
                  Your account is not linked to a recruiter profile. Contact your administrator.
                </p>
              </>
            ) : (
              <p className="text-sm text-red-600">{error}</p>
            )}
          </div>
        </Card>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <Spinner label="Loading your commission data…" />
        </div>
      )}

      {data && !('error' in data) && !loading && <CommissionBody data={data} goals={goals} />}
    </Shell>
  );
}

function CommissionBody({ data, goals }: { data: MyCommissionData; goals: GoalPortalData | null }) {
  const { recruiter, ytd, thisWeek, priorWeek, tiers, contractors, trend } = data;
  const periodGp = toPlotValue(ytd.grossProfit);
  const tierThresholds = tiers.map((t) => toPlotValue(t.threshold));
  const maxTier = tierThresholds.length > 0 ? tierThresholds[tierThresholds.length - 1]! : 0;
  const progressPct = maxTier > 0 ? Math.min(100, (periodGp / (maxTier * 1.2)) * 100) : 0;
  const commRate = periodGp > 0 ? (toPlotValue(ytd.commission) / periodGp) * 100 : 0;
  const tierPct = toPlotValue(ytd.currentTierRate) * 100;
  const gpSpark = trend.map((w) => toPlotValue(w.grossProfit));
  const hoursSpark = trend.map((w) => toPlotValue(w.hours));
  const gpPerHourSpark = trend.map((w) => perHour(w.grossProfit, w.hours));
  const maxGp = Math.max(...gpSpark, 1);
  const nextThreshold =
    ytd.nextTierGap !== null ? periodGp + toPlotValue(ytd.nextTierGap) : periodGp;
  const towardNext = nextThreshold > 0 ? (periodGp / nextThreshold) * 100 : 100;

  return (
    <>
      {recruiter.role === 'salesperson' && (
        <Card className="mb-4 border-l-4 border-l-caution">
          <div className="px-4 py-3">
            <p className="text-sm font-semibold text-ink">Commission transition rules pending</p>
            <p className="mt-1 text-sm text-ink-muted">
              Sales commission currently uses the standard plan. Accelerated programs, guaranteed
              commissions, and role-change tiers are waiting on Jamie&apos;s if-then rules and will
              be configured as a separate transition system — they are not hardcoded here.
            </p>
          </div>
        </Card>
      )}

      <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          label="Gross Profit"
          value={moneyCompact(ytd.grossProfit)}
          exact={money(ytd.grossProfit)}
          subtitle={`${String(ytd.contractorCount)} contractor${ytd.contractorCount === 1 ? '' : 's'} in range`}
          tone="profit"
          delay={0}
          sparkline={gpSpark.length > 1 ? gpSpark : undefined}
          sparklineColor="#16a34a"
          sparklineLabel="GP/wk"
        />
        <KpiCard
          label="Commission Earned"
          value={moneyCompact(ytd.commission)}
          exact={money(ytd.commission)}
          subtitle={`${tierPct.toFixed(0)}% tier rate · ${commRate.toFixed(1)}% of GP`}
          tone={commRate > 15 ? 'caution' : 'revenue'}
          delay={40}
        />
        <KpiCard
          label="Latest week"
          value={thisWeek ? moneyCompact(thisWeek.grossProfit) : '—'}
          exact={thisWeek ? money(thisWeek.grossProfit) : undefined}
          subtitle={
            thisWeek
              ? `${shortDate(thisWeek.weekEnding)} · ${hours(thisWeek.hours)}h · ${String(thisWeek.contractors)} ctrs`
              : 'No week in this range'
          }
          tone="profit"
          delay={80}
          trend={
            thisWeek && priorWeek
              ? computeTrend(toPlotValue(thisWeek.grossProfit), toPlotValue(priorWeek.grossProfit))
              : undefined
          }
          trendContext={priorWeek ? `vs ${shortDate(priorWeek.weekEnding)}` : undefined}
          sparkline={gpSpark.length > 1 ? gpSpark : undefined}
          sparklineColor="#16a34a"
          sparklineLabel="GP/wk"
        />
        <KpiCard
          label="Avg GP/Hour"
          value={ytd.gpPerHour === null ? '—' : rate(ytd.gpPerHour)}
          exact={ytd.gpPerHour === null ? undefined : rate(ytd.gpPerHour)}
          delay={120}
          sparkline={gpPerHourSpark.length > 1 ? gpPerHourSpark : undefined}
          sparklineColor="#1c1c1a"
          sparklineLabel="$/hr"
        />
        <KpiCard
          label="Hours"
          value={hours(ytd.hours)}
          subtitle={`${String(trend.length)} week${trend.length === 1 ? '' : 's'} in range`}
          delay={160}
          sparkline={hoursSpark.length > 1 ? hoursSpark : undefined}
          sparklineColor="#2563eb"
          sparklineLabel="hrs/wk"
        />
        <KpiCard
          label="Next Tier"
          value={ytd.nextTierGap ? moneyCompact(ytd.nextTierGap) : 'Top tier'}
          exact={ytd.nextTierGap ? money(ytd.nextTierGap) : undefined}
          subtitle={ytd.nextTierGap ? 'GP needed to advance' : 'Maximum rate achieved'}
          tone={ytd.nextTierGap ? 'caution' : 'profit'}
          delay={200}
          footer={
            <FillBar
              pct={towardNext}
              left={moneyCompact(ytd.grossProfit)}
              right={ytd.nextTierGap ? moneyCompact(String(nextThreshold)) : 'top'}
              tone={ytd.nextTierGap ? 'caution' : 'profit'}
            />
          }
        />
      </div>

      {goals !== null && !goals.missingPlan && goals.progress !== null && (
        <div className="mb-4">
          <GoalProgress
            progress={goals.progress}
            title={`${String(goals.year)} personal GP goal`}
          />
          <GoalWeekTable
            weeks={goals.progress.weeks}
            asOfWeekNumber={goals.progress.asOfWeekNumber}
          />
        </div>
      )}

      {tiers.length > 0 && (
        <Card className="mb-4">
          <CardHeader
            title="Commission Tier Progress"
            subtitle={`${ytd.tierMode === 'marginal' ? 'Marginal' : 'Flat'} calculation`}
          />
          <div className="px-4 pb-4">
            <div className="relative mb-2 h-8 overflow-hidden rounded-full bg-surface-overlay">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-profit/70"
                style={{ width: `${String(progressPct)}%` }}
              />
              {tiers.map((tier) => {
                const pos = maxTier > 0 ? (toPlotValue(tier.threshold) / (maxTier * 1.2)) * 100 : 0;
                return (
                  <div
                    key={tier.threshold}
                    className="absolute top-0 bottom-0 w-px bg-edge"
                    style={{ left: `${String(pos)}%` }}
                    title={`${money(tier.threshold)} → ${(toPlotValue(tier.rate) * 100).toFixed(0)}%`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-ink-faint">
              {tiers.map((tier, i) => (
                <span
                  key={tier.threshold}
                  className={ytd.currentTierIndex >= i ? 'font-semibold text-profit' : ''}
                >
                  {(toPlotValue(tier.rate) * 100).toFixed(0)}%
                  <br />
                  {moneyCompact(tier.threshold)}
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}

      {trend.length > 0 && (
        <Card className="mb-4">
          <CardHeader
            title="Weekly GP Trend"
            subtitle={`${String(trend.length)} week${trend.length === 1 ? '' : 's'} in the selected range`}
          />
          <div className="overflow-x-auto px-4 pb-4">
            <div
              className="flex items-end gap-2"
              style={{ minWidth: `${String(Math.max(trend.length * 3.25, 24))}rem` }}
            >
              {trend.map((w) => {
                const barPx = Math.max(8, (toPlotValue(w.grossProfit) / maxGp) * 140);
                return (
                  <div
                    key={w.weekEnding}
                    className="flex min-w-[2.75rem] flex-1 flex-col items-center gap-1"
                  >
                    <span className="text-[9px] font-semibold text-ink">
                      {moneyCompact(w.grossProfit)}
                    </span>
                    <div
                      className="w-full rounded-t bg-profit/70"
                      style={{ height: `${String(barPx)}px` }}
                      title={`${shortDate(w.weekEnding)}: ${money(w.grossProfit)}`}
                    />
                    <span className="text-[8px] text-ink-faint">{shortDate(w.weekEnding)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="My Contractors" subtitle={`${String(contractors.length)} assigned`} />
        {contractors.length > 0 ? (
          <Table
            head={[
              { label: 'Contractor' },
              { label: 'Client' },
              { label: 'Share', align: 'right' as const },
              { label: 'Hours', align: 'right' as const },
              { label: 'GP/Hr', align: 'right' as const },
              { label: 'Gross Profit', align: 'right' as const },
            ]}
          >
            {contractors.map((c) => (
              <Row key={c.workerId}>
                <Cell>{c.name}</Cell>
                <Cell muted>{c.clientName}</Cell>
                <Cell align="right" mono>
                  {(toPlotValue(c.share) * 100).toFixed(0)}%
                </Cell>
                <Cell align="right" mono>
                  {hours(c.hours)}
                </Cell>
                <Cell align="right" mono>
                  {c.gpPerHour ? money(c.gpPerHour) : '—'}
                </Cell>
                <Cell align="right" mono>
                  {money(c.grossProfit)}
                </Cell>
              </Row>
            ))}
          </Table>
        ) : (
          <p className="px-4 pb-4 text-sm text-ink-muted">No contractors in this date range.</p>
        )}
      </Card>
    </>
  );
}
